import { darken, lighten, SxProps, Theme } from "@mui/material";

import { getIconSize } from "../helpers/iconUtils";

import { ButtonSize, ButtonVibe } from "./types";

import { IconSize } from "@/common/types/mui";

export const ICON_SIZE_MAP: Record<ButtonSize, IconSize> = Object.freeze({
  small: "small",
  medium: "small",
  large: "medium",
});

export const getRootSx = (theme: Theme): SxProps<Theme> => {
  return {
    gap: 0.5,
    borderRadius: 1,
    transition: "background-color 200ms ease-out, outline 100ms ease-out",
    outline: "0px solid transparent",
    ["&:focus-visible"]: {
      outline: `3px solid ${theme.palette.border.brand.moderate}`,
    },
  };
};

export const getSizeSx = (theme: Theme, size: ButtonSize): SxProps<Theme> => {
  return {
    ...(size === "small" && {
      minHeight: theme.button.size.default.small,
      padding: theme.spacing(0.5, 2),
      ...theme.typography.button2,
      lineHeight: `${getIconSize(theme, ICON_SIZE_MAP[size])}px`,
    }),
    ...(size === "medium" && {
      minHeight: theme.button.size.default.medium,
      padding: theme.spacing(1, 2),
      ...theme.typography.button2,
      lineHeight: `${getIconSize(theme, ICON_SIZE_MAP[size])}px`,
    }),
    ...(size === "large" && {
      minHeight: theme.button.size.default.large,
      padding: theme.spacing(1, 2),
      ...theme.typography.button1,
      lineHeight: `${getIconSize(theme, ICON_SIZE_MAP[size])}px`,
    }),
  };
};

export const getFilledSx = (theme: Theme, vibeKey: ButtonVibe): SxProps<Theme> => {
  const backgroundColor =
    vibeKey === "alternate" ? theme.palette.surface.alternate : theme.palette.surface[vibeKey].intense;
  const color = vibeKey === "alternate" ? theme.palette.text.alternate : theme.palette.text.reverse.intense;

  return {
    backgroundColor,
    color,
    ["&:hover"]: {
      backgroundColor: darken(backgroundColor, 0.3),
    },
  };
};

export const getTonalSx = (theme: Theme, vibeKey: ButtonVibe): SxProps<Theme> => {
  const backgroundColor =
    vibeKey === "alternate" ? lighten(theme.palette.surface.alternate, 0.3) : theme.palette.surface[vibeKey].moderate;
  const color = vibeKey === "alternate" ? theme.palette.text.alternate : theme.palette.text[vibeKey].intense;

  return {
    backgroundColor,
    color,
    ["&:hover"]: {
      backgroundColor: darken(backgroundColor, 0.1),
    },
  };
};

export const getOutlinedSx = (theme: Theme, vibeKey: ButtonVibe): SxProps<Theme> => {
  const borderColor = vibeKey === "alternate" ? theme.palette.border.alternate : theme.palette.border[vibeKey].moderate;
  const color = vibeKey === "alternate" ? theme.palette.text.alternate : theme.palette.text[vibeKey].intense;
  const backgroundColor =
    vibeKey === "alternate" ? lighten(theme.palette.surface.alternate, 0.6) : theme.palette.surface[vibeKey].quiet;

  return {
    border: `1px solid ${borderColor}`,
    color,
    ["&:hover"]: {
      backgroundColor,
    },
  };
};

export const getTextSx = (theme: Theme, vibeKey: ButtonVibe): SxProps<Theme> => {
  const backgroundColor =
    vibeKey === "alternate" ? lighten(theme.palette.surface.alternate, 0.6) : theme.palette.surface[vibeKey].quiet;
  const color = vibeKey === "alternate" ? theme.palette.text.alternate : theme.palette.text[vibeKey].moderate;
  const hoverColor =
    vibeKey === "alternate" ? darken(theme.palette.text.alternate, 0.1) : theme.palette.text[vibeKey].intense;

  return {
    color,
    ["&:hover"]: {
      backgroundColor,
      color: hoverColor,
    },
  };
};
